<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useUserStore } from '@/stores/user'
import { useRouter } from 'vue-router'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Search, Plus } from 'lucide-vue-next'
import OrganizationsList from './OrganizationsList.vue'
import DialogCreateOrg from './dialogs/DialogCreateOrg.vue'
import AdminNavigation from '@/components/admin/AdminNavigation.vue'
import type { AdminOrganization } from '@/types/admin'

const router = useRouter()
const userStore = useUserStore()
const member = computed(() => userStore.member)

// Default feature configuration for organizations
const DEFAULT_FEATURE_FLAGS = {
  static: true,
  collected_data: true,
  radar: false,
  planet_vars: false,
  planet_order: false
}

const organizations = ref<AdminOrganization[]>([])
const loading = ref(false)
const searchQuery = ref('')
const limit = ref(100)
const offset = ref(0)
const total = ref(0)
let debounceTimer: NodeJS.Timeout | null = null

const filteredOrganizations = computed(() => {
  if (!searchQuery.value) {
    return organizations.value
  }
  return organizations.value.filter(org =>
    org.organization_name.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const fetchOrganizations = async () => {
  loading.value = true
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/organizations`,
      params: {
        limit: limit.value,
        offset: offset.value,
        search: searchQuery.value || undefined
      }
    })
    organizations.value = response.data
    total.value = response.data.length
  } catch (error) {
    toast({
      title: 'Error',
      description: `Failed to fetch organizations: ${error instanceof Error ? error.message : String(error)}`,
      variant: 'destructive'
    })
  } finally {
    loading.value = false
  }
}

const debouncedFetchOrganizations = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }
  debounceTimer = setTimeout(() => {
    fetchOrganizations()
  }, 300)
}

watch(searchQuery, () => {
  debouncedFetchOrganizations()
})

const onOrganizationUpdated = () => {
  fetchOrganizations()
}

onMounted(() => {
  if (!member.value?.is_super_user) {
    router.push({ name: 'Dashboard' })
    return
  }
  fetchOrganizations()
})
</script>

<template>
  <div v-if="member && member.is_super_user" class="h-full flex flex-col">
    <AdminNavigation />
    <div class="p-6 space-y-4 flex-1 overflow-auto">
    <div class="flex items-center justify-between">
      <h1 class="text-2xl font-bold">Organizations</h1>
    </div>

    <div class="flex items-center gap-4">
      <div class="relative flex-1 max-w-md">
        <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          v-model="searchQuery"
          placeholder="Search organizations..."
          class="pl-10"
        />
      </div>
      <DialogCreateOrg @organization-created="(orgId) => router.push({ name: 'OrgMembers', params: { org_id: orgId }, query: { new: '1' } })">
        <Button variant="default">
          <Plus class="mr-2 h-4 w-4" />
          Add Organization
        </Button>
      </DialogCreateOrg>
    </div>

    <div class="text-sm text-muted-foreground">
      Total organizations: {{ total }}
    </div>

    <OrganizationsList
      :organizations="filteredOrganizations"
      :loading="loading"
      :default-feature-flags="DEFAULT_FEATURE_FLAGS"
      @organization-updated="onOrganizationUpdated"
    />
    </div>
  </div>
</template>
